import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import appLogo from '../assets/icon-noBG.png';
import { CONTACT_EMAIL, WHATSAPP_NUMBERS } from '../utils/contact';

// ── Icons (same hand-drawn stroke convention as AppLayout.tsx — no icon
// library is installed). Related feature cards intentionally share an icon
// (e.g. Split & Merge / Stock Transfer both use IconSwap) rather than
// inventing a bespoke glyph per line item. ──────────────────────────────────

function IconBoxL() {
  return <svg width="20" height="20" viewBox="0 0 16 16" fill="none">
    <path d="M8 1.5L1.5 5v6L8 14.5 14.5 11V5L8 1.5z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    <path d="M1.5 5L8 8.5 14.5 5M8 8.5V14.5" stroke="currentColor" strokeWidth="1.4" />
  </svg>;
}
function IconSwap() {
  return <svg width="20" height="20" viewBox="0 0 16 16" fill="none">
    <path d="M2 5h9l-2.5-2.5M14 11H5l2.5 2.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}
function IconSliders() {
  return <svg width="20" height="20" viewBox="0 0 16 16" fill="none">
    <path d="M2 4.5h5.5M10.5 4.5H14M2 11.5h3M8 11.5h6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <circle cx="9" cy="4.5" r="1.9" stroke="currentColor" strokeWidth="1.4" />
    <circle cx="6.5" cy="11.5" r="1.9" stroke="currentColor" strokeWidth="1.4" />
  </svg>;
}
function IconArrowOut() {
  return <svg width="20" height="20" viewBox="0 0 16 16" fill="none">
    <path d="M4 12L12 4M12 4H6M12 4V10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}
function IconArrowIn() {
  return <svg width="20" height="20" viewBox="0 0 16 16" fill="none">
    <path d="M12 4L4 12M4 12H10M4 12V6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}
function IconDocument() {
  return <svg width="20" height="20" viewBox="0 0 16 16" fill="none">
    <path d="M4 1.5h5.5L13 5v9.5H4Z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    <path d="M9 1.5V5h4M6 8h4M6 10.5h4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
  </svg>;
}
function IconUndo() {
  return <svg width="20" height="20" viewBox="0 0 16 16" fill="none">
    <path d="M4 4.5H10a3.5 3.5 0 010 7H7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <path d="M6 2 3.5 4.5 6 7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}
function IconReceipt() {
  return <svg width="20" height="20" viewBox="0 0 16 16" fill="none">
    <path d="M4 1.5h8v13l-1.5-1-1.5 1-1.5-1-1.5 1-1.5-1-1.5 1Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    <path d="M6 5h4M6 7.5h4M6 10h2.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
  </svg>;
}
function IconPercent() {
  return <svg width="20" height="20" viewBox="0 0 16 16" fill="none">
    <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.4" />
    <path d="M5.5 5.5l5 5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    <circle cx="6.1" cy="5.9" r=".9" fill="currentColor" />
    <circle cx="9.9" cy="10.1" r=".9" fill="currentColor" />
  </svg>;
}
function IconChart() {
  return <svg width="22" height="22" viewBox="0 0 16 16" fill="none">
    <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.4" />
    <path d="M8 4.5v7M10.2 6.2c0-.9-.98-1.7-2.2-1.7-1.22 0-2.2.76-2.2 1.7 0 .94.98 1.3 2.2 1.5 1.22.2 2.2.56 2.2 1.5 0 .94-.98 1.7-2.2 1.7-1.22 0-2.2-.8-2.2-1.7"
      stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}
function IconLedgerBook() {
  return <svg width="22" height="22" viewBox="0 0 16 16" fill="none">
    <path d="M3.5 2.5h9v11h-9Z" stroke="currentColor" strokeWidth="1.4" />
    <path d="M6 2.5v11M3.5 6h2.5M3.5 9.5h2.5M8 6h3M8 9h3" stroke="currentColor" strokeWidth="1.3" />
  </svg>;
}
function IconBankL() {
  return <svg width="22" height="22" viewBox="0 0 16 16" fill="none">
    <rect x="1.5" y="6.5" width="13" height="7" rx="1" stroke="currentColor" strokeWidth="1.4" />
    <path d="M1.5 6.5l6.5-4 6.5 4" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    <path d="M4 9.5v2.5M8 9.5v2.5M12 9.5v2.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>;
}
function IconUsersL() {
  return <svg width="20" height="20" viewBox="0 0 16 16" fill="none">
    <circle cx="5.5" cy="5" r="2.3" stroke="currentColor" strokeWidth="1.3" />
    <circle cx="11.2" cy="6" r="1.8" stroke="currentColor" strokeWidth="1.3" />
    <path d="M1.3 13.2c0-2.1 1.9-3.7 4.2-3.7s4.2 1.6 4.2 3.7" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    <path d="M9.7 9.7c1.9.25 3.4 1.6 3.4 3.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
  </svg>;
}
function IconShieldL() {
  return <svg width="20" height="20" viewBox="0 0 16 16" fill="none">
    <path d="M8 1.5l5.5 2v4c0 4-2.4 6.3-5.5 7-3.1-.7-5.5-3-5.5-7v-4L8 1.5z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    <path d="M5.7 8l1.6 1.6 3-3.2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}
function IconCloudL() {
  return <svg width="20" height="20" viewBox="0 0 16 16" fill="none">
    <path d="M4.5 12.5h7.2a2.8 2.8 0 00.4-5.6A4 4 0 004.4 6a3.3 3.3 0 00.1 6.5z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
  </svg>;
}
function IconDatabaseL() {
  return <svg width="20" height="20" viewBox="0 0 16 16" fill="none">
    <ellipse cx="8" cy="3.8" rx="5" ry="2" stroke="currentColor" strokeWidth="1.4" />
    <path d="M3 3.8v8.4c0 1.1 2.2 2 5 2s5-.9 5-2V3.8M3 8c0 1.1 2.2 2 5 2s5-.9 5-2" stroke="currentColor" strokeWidth="1.4" />
  </svg>;
}
function IconKeyL() {
  return <svg width="20" height="20" viewBox="0 0 16 16" fill="none">
    <rect x="4" y="1.5" width="8" height="13" rx="1.6" stroke="currentColor" strokeWidth="1.4" />
    <path d="M6.6 8.2l1 1 1.9-2.1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}
function IconDownloadL() {
  return <svg width="20" height="20" viewBox="0 0 16 16" fill="none">
    <path d="M8 2v8M5 7.2L8 10.2l3-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M2.5 11v1.5a1 1 0 001 1h9a1 1 0 001-1V11" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>;
}
function IconPalette() {
  return <svg width="20" height="20" viewBox="0 0 16 16" fill="none">
    <path d="M8 1.5a6.5 6.5 0 100 13c.9 0 1.5-.7 1.5-1.5 0-.4-.15-.7-.4-1-.25-.3-.4-.6-.4-1 0-.8.7-1.5 1.5-1.5H12c1.4 0 2.5-1.1 2.5-2.5A5.5 6 0 008 1.5z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    <circle cx="4.8" cy="7" r=".9" fill="currentColor" />
    <circle cx="6.5" cy="4.5" r=".9" fill="currentColor" />
    <circle cx="9.7" cy="4.5" r=".9" fill="currentColor" />
  </svg>;
}
function IconMail() {
  return <svg width="22" height="22" viewBox="0 0 16 16" fill="none">
    <rect x="1.5" y="3" width="13" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
    <path d="M2 4l6 5 6-5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}
// WhatsApp logo (Simple Icons, CC0), drawn in currentColor.
function IconWhatsApp() {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
  </svg>;
}

function IconPhone() {
  return <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M3.5 1.5h2.2l1.1 3-1.5 1a8 8 0 004.2 4.2l1-1.5 3 1.1v2.2a1.5 1.5 0 01-1.6 1.5A11.5 11.5 0 012 3.1 1.5 1.5 0 013.5 1.5z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
  </svg>;
}
function IconArrowDown() {
  return <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
    <path d="M8 2.5v10M4 8.5l4 4 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}
function IconGemSmall() {
  return <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M4 2h8l3 4-7 8-7-8 3-4z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    <path d="M1 6h14M6 2L5.2 6 8 14M10 2l.8 4L8 14" stroke="currentColor" strokeWidth="1.1" strokeLinejoin="round" />
  </svg>;
}
// ── Scroll-triggered reveal ──────────────────────────────────────────────
// A section (or a single card inside a grid) fades + rises into place the
// first time it enters the viewport, then stays — nothing re-triggers on
// scroll-back-up. `delay` staggers cards within the same grid a beat apart,
// same technique as the hero's own load-in animation in index.css.
function Reveal({ children, delay = 0, className = '' }: { children: ReactNode; delay?: number; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) { setVisible(true); obs.disconnect(); }
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`landing-reveal${visible ? ' landing-reveal-visible' : ''}${className ? ` ${className}` : ''}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

// Padlock that keeps locking: it starts open, snaps shut (the shackle
// swings down, the body gives a small "click", a soft ring pulses out),
// holds, opens again and relocks, for as long as it's on screen. It pauses
// off screen, and under reduced motion it simply shows locked.
const LOCK_HOLD_MS = 2600;   // how long it stays locked each round
const LOCK_OPEN_MS = 900;    // how long it stays open before locking again

function LockMark({ size = 26 }: { size?: number }) {
  const ref = useRef<HTMLSpanElement>(null);
  const reduce = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const [locked, setLocked] = useState(reduce);

  useEffect(() => {
    const el = ref.current;
    if (!el || reduce) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const stop = () => { if (timer) clearTimeout(timer); timer = undefined; };
    // open → (LOCK_OPEN_MS) → locked → (LOCK_HOLD_MS) → open → …
    const lockNext = () => { timer = setTimeout(() => { setLocked(true); openNext(); }, LOCK_OPEN_MS); };
    const openNext = () => { timer = setTimeout(() => { setLocked(false); lockNext(); }, LOCK_HOLD_MS); };
    const obs = new IntersectionObserver(([e]) => {
      stop();
      if (e.isIntersecting) { setLocked(false); lockNext(); }
    }, { threshold: 0.6 });
    obs.observe(el);
    return () => { obs.disconnect(); stop(); };
  }, [reduce]);

  return (
    <span ref={ref} className={`landing-lock${locked ? ' is-locked' : ''}`} aria-hidden="true">
      <svg viewBox="0 0 24 24" width={size} height={size} fill="none">
        <path className="landing-lock-shackle" d="M8 11V7.6a4 4 0 0 1 8 0V11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <g className="landing-lock-body">
          <rect x="5" y="10.5" width="14" height="10.5" rx="2.6" fill="currentColor" />
          <circle cx="12" cy="15" r="1.5" className="landing-lock-hole" />
          <path d="M12 15.5v2.2" strokeWidth="1.6" strokeLinecap="round" className="landing-lock-hole-line" />
        </g>
      </svg>
    </span>
  );
}

// ── Content ──────────────────────────────────────────────────────────────

// "Request a demo": opens WhatsApp with the message already typed, so the
// visitor only has to press send (no form or server behind this page).
const DEMO_URL = `https://wa.me/${WHATSAPP_NUMBERS[0].wa}?text=${encodeURIComponent("Hi, I'd like a demo of DiaWolt.")}`;

interface Feature { icon: ReactNode; title: string; desc: string }

// One colour per card, in order, so neighbouring icons never match.
const ICON_COLORS = [
  'var(--primary)', 'var(--success)', 'var(--warning)', 'var(--accent-violet)',
  'var(--accent-teal)', 'var(--danger)', 'var(--accent-pink)', 'var(--accent-sky)',
];
const iconColor = (i: number) => ({ '--icon-color': ICON_COLORS[i % ICON_COLORS.length] }) as React.CSSProperties;

// "Your data is safe": the layers around a company's data, outermost
// first. Only claims that are true today; strengthen the wording once the
// company databases are locked to signed in users.
const SAFETY_LAYERS: Feature[] = [
  { icon: <IconCloudL />,    title: 'Google Cloud',          desc: 'Stored on Google Cloud, encrypted while it travels and while it is stored.' },
  { icon: <IconDatabaseL />, title: 'Your own database',     desc: 'Your company\'s data is kept in a database of its own.' },
  { icon: <IconKeyL />,      title: 'Two step sign in',      desc: 'Sign in with your password and a code from Google or Microsoft Authenticator.' },
  { icon: <IconUsersL />,    title: 'Staff see what you allow', desc: 'You choose, page by page, what each staff member can open.' },
  { icon: <IconDocument />,  title: 'Who did what',          desc: 'Every action on a sale or purchase is recorded, with who did it and when.' },
  { icon: <IconDownloadL />, title: 'Your own backups',      desc: 'Download a full copy of your data whenever you like.' },
];

// "Request a demo" band: how the demo works.
const DEMO_STEPS: { title: string; desc: string }[] = [
  { title: 'Send us a message', desc: 'Tap Request a demo. The WhatsApp message is already written.' },
  { title: 'Pick a time', desc: 'A 20 minute call on WhatsApp or video, whenever suits you.' },
  { title: 'See your own stock', desc: 'We add a few of your stones and show you every step.' },
];

// The hero's facts bar: one floating pill, a divider between each fact.
const HIGHLIGHTS: { icon: ReactNode; label: string }[] = [
  { icon: <IconBoxL />,       label: 'Certified & loose' },
  { icon: <IconArrowOut />,   label: 'Memo in & out' },
  { icon: <IconLedgerBook />, label: 'Double entry books' },
  { icon: <IconDocument />,   label: 'Excel import' },
];

const INVENTORY_FEATURES: Feature[] = [
  { icon: <IconBoxL />, title: 'Full Stock List', desc: 'Certified and loose diamonds, searchable, exportable, with columns you choose.' },
  { icon: <IconSwap />, title: 'Split & Merge', desc: 'Recut, reprice or combine lots. The average cost is worked out for you.' },
  { icon: <IconSwap />, title: 'Stock Transfer', desc: 'Move stones between vaults or locations without touching their value.' },
  { icon: <IconSliders />, title: 'Stock Adjustment', desc: 'Record a loss or gain from a physical count, with the reason on file.' },
  { icon: <IconSliders />, title: 'Stock Revaluation', desc: 'Reprice a lot to a new carrying value, and the change flows straight to Profit.' },
  { icon: <IconBoxL />, title: 'Opening Stock', desc: 'Bring in the inventory you already own the day you switch to DiaWolt.' },
  { icon: <IconArrowOut />, title: 'Memo Out', desc: 'Send stones to a client on consignment, and track what\'s out, overdue, and returned.' },
  { icon: <IconArrowIn />, title: 'Memo In', desc: 'Receive stones from a supplier before you buy, then convert to a purchase or send them back.' },
  { icon: <IconDocument />, title: 'Stone History', desc: 'Every stone keeps one timeline: bought, sent on memo, returned, sold.' },
  { icon: <IconLedgerBook />, title: 'Books & Dummy Code', desc: 'Keep your Books and Real stock in step with one simple code.' },
];

// Purchase & Sales: two lanes (buy / sell) plus what works for both.
const BUY_STEPS: Feature[] = [
  { icon: <IconDocument />, title: 'Purchase Invoices', desc: 'Certified, loose or mixed. Numbered for you, with a unique code for every stone.' },
  { icon: <IconUndo />, title: 'Returns & Debit Notes', desc: 'Send goods back or claim a price change. What you owe updates itself.' },
  { icon: <IconReceipt />, title: 'Purchase Expenses', desc: 'Freight, duty and certificates, added to stock cost or booked as an expense.' },
];
const SELL_STEPS: Feature[] = [
  { icon: <IconDocument />, title: 'Sales Invoices', desc: 'Sell part of a parcel and keep track of the rest, to the carat.' },
  { icon: <IconReceipt />, title: 'Sales Expenses', desc: 'Shipping and insurance on a sale. Pay it yourself, or charge the customer.' },
  { icon: <IconUndo />, title: 'Returns & Credit Notes', desc: 'A stone comes back. The credit and the restock happen together.' },
];
const BOTH_FEATURES: Feature[] = [
  { icon: <IconPercent />, title: 'Broker Commission', desc: 'Up to two brokers per stone.' },
  { icon: <IconBoxL />, title: 'Excel Import', desc: 'Bring in purchases, stock and customers.' },
];

const ACCOUNTING_POINTS: { icon: ReactNode; title: string; desc: string; color: number; tag?: string }[] = [
  { icon: <IconChart />,      color: 1, title: 'For your team',       desc: 'Profit, expenses and who owes who, in plain words. Updated the moment a sale or payment happens.' },
  { icon: <IconLedgerBook />, color: 3, title: 'For your accountant', desc: 'Real double entry books underneath: Journal, General Ledger and Trial Balance.', tag: 'Owner only' },
  { icon: <IconBankL />,      color: 4, title: 'Banking, handled',    desc: 'Every bank and cash balance at a glance. Money in and out by month or by day.' },
  { icon: <IconChart />,      color: 7, title: 'Reports',             desc: 'Sales, purchases, money owed and statements. Print or save as PDF.' },
];

const TEAM_CLUSTERS: { icon: ReactNode; title: string; items: string[] }[] = [
  { icon: <IconUsersL />, title: 'Your business, organized', items: ['Companies, customers, brokers & KYC', 'Import and export customers with Excel', 'Reminders and a dashboard you can customize'] },
  { icon: <IconShieldL />, title: 'Secure by design', items: ['Every business gets its own isolated database', 'Owner and staff, with access page by page', 'Signs out by itself when left idle'] },
  { icon: <IconPalette />, title: 'Feels like yours', items: ['Light or dark theme', 'Wallpapers and a custom accent color', 'Choose which columns every table shows'] },
];

const FAQS: { q: string; a: string }[] = [
  { q: 'Do I need to be an accountant to use DiaWolt?', a: 'No. The screens use plain business words: money in, money out, what you owe and what is owed to you. Real double entry books run in the background for your accountant.' },
  { q: 'Can my staff use it too?', a: 'Yes. The owner grants each staff login access to exactly the pages they need, page by page, and nothing more.' },
  { q: 'Is my data shared with other businesses on DiaWolt?', a: 'No. Every business has its own separate database. Nothing is ever shared or mixed.' },
  { q: 'Can I bring in my existing customers and stock?', a: 'Yes. Download the sample Excel file, fill it in and upload it. Customers, stock, purchases and memos can all be imported.' },
  { q: 'Can I track stones I send out or receive on consignment?', a: 'Yes. Memo Out tracks stones sent to a client (overdue, returned, extended), and Memo In tracks stones a supplier consigns to you before you buy.' },
  { q: 'Does it handle certified and loose diamonds differently?', a: 'Yes. Both work side by side, each with their own fields. Every stone gets its own unique code, so nothing gets lost.' },
  { q: 'Can the yearly price change?', a: 'It can go up, but never by surprise. We tell you at least 3 months before your renewal, and a year you have already paid for never changes. If you would rather not continue, you keep full access until the end of your paid year.' },
  { q: 'How do I get started?', a: 'Reach out through the Contact section below and we\'ll get you set up.' },
];

// ── "See it in action" — real app screenshots ───────────────────────────────
// Images live in public/screenshots/<key>.png (see the README there). Until a
// file is added, its tab shows a tidy placeholder instead of a broken image.
const SCREENS: { key: string; label: string; caption: string }[] = [
  { key: 'dashboard', label: 'Dashboard', caption: 'Your day at a glance: stock, sales, money owed and bank balances.' },
  { key: 'inventory', label: 'Inventory', caption: 'Every stone, loose or certified, with the columns you choose.' },
  { key: 'reports',   label: 'Reports',   caption: 'Sales, purchases, money owed and statements, ready to print.' },
  { key: 'sales',     label: 'Sales',     caption: 'Add stones by Code, set the price, then print the invoice.' },
  { key: 'purchase',  label: 'Purchase',  caption: 'Every supplier invoice with its stones, due dates and payment status.' },
];
// How long each screen stays before the next one slides in. The progress
// line on the active tab runs for this long; its animationend moves on, so
// pausing the line (hover, off screen) pauses the rotation too.
const SHOT_MS = 5000;

function ScreensShowcase() {
  const [index, setIndex] = useState(0);
  const [dir, setDir] = useState<'next' | 'prev'>('next');
  const [missing, setMissing] = useState<Set<string>>(new Set());
  // Auto play stops for good once the visitor picks a tab themselves.
  const [auto, setAuto] = useState(() =>
    typeof window === 'undefined' || !window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  const [hovered, setHovered] = useState(false);
  const [inView, setInView] = useState(false);
  // Full screen view (click the picture). Shares `index` with the tabs, so
  // closing it leaves the tab on the picture last shown.
  const [zoom, setZoom] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const screen = SCREENS[index];
  const src = (key: string) => `${import.meta.env.BASE_URL}screenshots/${key}.png`;

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => setInView(e.isIntersecting), { threshold: 0.35 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // Load the next screen early so it's ready before it slides in.
  useEffect(() => {
    if (!inView) return;
    const img = new Image();
    img.src = `${import.meta.env.BASE_URL}screenshots/${SCREENS[(index + 1) % SCREENS.length].key}.png`;
  }, [index, inView]);

  const go = (i: number, d: 'next' | 'prev') => { setDir(d); setIndex(i); };
  const pick = (i: number) => {
    setAuto(false);
    if (i !== index) go(i, i > index ? 'next' : 'prev');
  };
  const advance = () => go((index + 1) % SCREENS.length, 'next');
  // Only pictures that exist can be stepped through full screen.
  const shown = SCREENS.map((s, i) => ({ s, i })).filter(x => !missing.has(x.s.key));
  const step = (d: 1 | -1) => {
    const at = shown.findIndex(x => x.i === index);
    const next = shown[(at + d + shown.length) % shown.length];
    if (next) go(next.i, d > 0 ? 'next' : 'prev');
  };
  const openZoom = () => { setAuto(false); setZoom(true); };
  const running = auto && inView && !hovered;

  return (
    <div ref={rootRef} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
      <div className="landing-shots-tabs" role="tablist">
        {SCREENS.map((s, i) => (
          <button
            key={s.key} type="button" role="tab" aria-selected={i === index}
            className={`landing-shots-tab${i === index ? ' active' : ''}`}
            onClick={() => pick(i)}
          >
            {s.label}
            {auto && i === index && (
              <span
                key={index} className="landing-shots-progress" aria-hidden="true"
                style={{ animationDuration: `${SHOT_MS}ms`, animationPlayState: running ? 'running' : 'paused' }}
                onAnimationEnd={advance}
              />
            )}
          </button>
        ))}
      </div>
      <div className="landing-shots-frame">
        <div className="landing-shots-bar">
          <span className="landing-shots-dot" /><span className="landing-shots-dot" /><span className="landing-shots-dot" />
          <span className="landing-shots-url">diawolt · {screen.label}</span>
        </div>
        <div className="landing-shots-stage">
          {missing.has(screen.key) ? (
            <div key={screen.key} className={`landing-shots-placeholder landing-shots-in-${dir}`}>
              <img src={appLogo} alt="" />
              <div>{screen.label} screenshot coming soon</div>
            </div>
          ) : (
            <button type="button" className="landing-shots-zoom" onClick={openZoom} aria-label={`View the ${screen.label} screen full size`}>
              <img
                key={screen.key} src={src(screen.key)} alt={`DiaWolt ${screen.label} screen`}
                className={`landing-shots-img landing-shots-in-${dir}`}
                onError={() => setMissing(m => new Set(m).add(screen.key))}
              />
              <span className="landing-shots-zoom-hint" aria-hidden="true">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                  <path d="M2 6V2h4M10 2h4v4M14 10v4h-4M6 14H2v-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                View full size
              </span>
            </button>
          )}
        </div>
      </div>
      <p key={screen.key} className="landing-shots-caption landing-shots-caption-in">{screen.caption}</p>
      {zoom && !missing.has(screen.key) && (
        <ShotLightbox
          src={src(screen.key)} label={screen.label} caption={screen.caption}
          pos={shown.findIndex(x => x.i === index) + 1} total={shown.length}
          onPrev={() => step(-1)} onNext={() => step(1)} onClose={() => setZoom(false)}
        />
      )}
    </div>
  );
}

// Full screen picture: dark backdrop, ‹ › arrows (and the arrow keys),
// Escape or a click outside the picture closes it, swipe on a phone.
function ShotLightbox({ src, label, caption, pos, total, onPrev, onNext, onClose }: {
  src: string; label: string; caption: string; pos: number; total: number;
  onPrev: () => void; onNext: () => void; onClose: () => void;
}) {
  const touchX = useRef<number | null>(null);
  const keys = useRef({ onPrev, onNext, onClose });
  useEffect(() => { keys.current = { onPrev, onNext, onClose }; });
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') keys.current.onClose();
      else if (e.key === 'ArrowLeft') keys.current.onPrev();
      else if (e.key === 'ArrowRight') keys.current.onNext();
    };
    window.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = overflow; };
  }, []);

  return createPortal(
    <div
      className="shot-lightbox" role="dialog" aria-modal="true" aria-label={`${label} screen`}
      onClick={onClose}
      onTouchStart={e => { touchX.current = e.touches[0].clientX; }}
      onTouchEnd={e => {
        if (touchX.current == null) return;
        const dx = e.changedTouches[0].clientX - touchX.current;
        touchX.current = null;
        if (Math.abs(dx) > 50) { if (dx > 0) onPrev(); else onNext(); }
      }}
    >
      <div className="shot-lightbox-top" onClick={e => e.stopPropagation()}>
        <span className="shot-lightbox-title">{label}<span className="shot-lightbox-count">{pos} / {total}</span></span>
        <button type="button" className="shot-lightbox-btn" onClick={onClose} aria-label="Close">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3.5 3.5l9 9M12.5 3.5l-9 9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
        </button>
      </div>
      <img key={src} src={src} alt={`DiaWolt ${label} screen`} className="shot-lightbox-img" onClick={e => e.stopPropagation()} />
      <p className="shot-lightbox-caption">{caption}</p>
      {total > 1 && <>
        <button type="button" className="shot-lightbox-btn shot-lightbox-arrow left" aria-label="Previous screen"
          onClick={e => { e.stopPropagation(); onPrev(); }}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M12.5 4l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <button type="button" className="shot-lightbox-btn shot-lightbox-arrow right" aria-label="Next screen"
          onClick={e => { e.stopPropagation(); onNext(); }}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M7.5 4l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
      </>}
    </div>,
    document.body,
  );
}

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`landing-faq-item${open ? ' open' : ''}`}>
      <button type="button" className="landing-faq-question" onClick={() => setOpen(o => !o)} aria-expanded={open}>
        {q}
        <span className="landing-faq-plus" aria-hidden="true" />
      </button>
      <div className="landing-faq-answer-wrap">
        <div className="landing-faq-answer-inner">
          <p className="landing-faq-answer">{a}</p>
        </div>
      </div>
    </div>
  );
}

export default function Landing() {
  const [navScrolled, setNavScrolled] = useState(false);

  // The landing page is a plain public page, independent of auth state — an
  // already-logged-in visitor can still browse it. Only clicking "Log In"
  // takes them to /login, which is what redirects a logged-in user straight
  // to /dashboard (see Login.tsx).

  // A slight shadow once the page scrolls, so the sticky header reads as its
  // own layer instead of just floating over the hero with a hard edge.
  useEffect(() => {
    const onScroll = () => setNavScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  function scrollToId(id: string) {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
  function scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  return (
    <div className="landing-page">
      <header className={`landing-nav${navScrolled ? ' landing-nav-scrolled' : ''}`}>
        <div className="landing-nav-inner">
          <button type="button" className="landing-brand landing-nav-brand" onClick={scrollToTop}>
            <img src={appLogo} alt="" className="landing-brand-icon" />
            <span className="landing-brand-text">DiaWolt</span>
          </button>

          <div className="landing-nav-actions">
            <button type="button" className="landing-nav-link" onClick={() => scrollToId('faq')}>FAQ</button>
            <button type="button" className="btn btn-secondary" onClick={() => scrollToId('contact')}>Request Demo</button>
            <Link to="/login" className="btn btn-primary">Log In</Link>
          </div>
        </div>
      </header>

      <section className="landing-hero">
        <div className="landing-texture landing-texture-dots" aria-hidden="true" />
        <div className="landing-hero-gem" aria-hidden="true" />
        <div className="landing-hero-inner">
          <span className="landing-hero-eyebrow"><IconGemSmall /> Made for diamond traders</span>
          <h1>
            Every stone tracked.
            <span className="landing-hero-accent">Every invoice in order.</span>
          </h1>
          <p className="landing-hero-sub">
            Stock, purchases, sales, memos, banking and the books, all in one place.
            Made for how diamond traders really work.
          </p>

          <div className="landing-hero-cta-row">
            <Link to="/login" className="btn btn-primary landing-hero-cta">Log In</Link>
            <button type="button" className="landing-hero-secondary" onClick={() => scrollToId('contact')}>
              Request Demo <IconArrowDown />
            </button>
          </div>

          {/* One floating pill with dividers, not separate chips. */}
          <div className="landing-facts-bar">
            {HIGHLIGHTS.map((h, i) => (
              <span key={h.label} className="landing-fact" style={iconColor(i)}>
                <span className="landing-fact-icon">{h.icon}</span>
                {h.label}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section id="screens" className="landing-section landing-section-alt">
        <div className="landing-section-inner">
          <Reveal>
            <span className="landing-section-eyebrow">See it in action</span>
            <h2>This is what DiaWolt looks like.</h2>
            <p className="landing-section-sub">Real screens from the app. Pick one to have a look.</p>
          </Reveal>
          <Reveal>
            <ScreensShowcase />
          </Reveal>
        </div>
      </section>

      <section id="inventory" className="landing-section">
        <div className="landing-texture landing-texture-dots" aria-hidden="true" />
        <div className="landing-section-inner">
          <Reveal>
            <span className="landing-section-eyebrow">Inventory</span>
            <h2>Know exactly what&apos;s in stock.</h2>
            <p className="landing-section-sub">From certified stones to loose goods, track every carat from arrival to sale.</p>
          </Reveal>
        </div>
        <Reveal>
          <div className="landing-carousel">
            <div className="landing-carousel-track">
              {[...INVENTORY_FEATURES, ...INVENTORY_FEATURES].map((f, i) => (
                <div
                  key={`${f.title}-${i}`}
                  className="landing-feature-card landing-carousel-card"
                  style={iconColor(i % INVENTORY_FEATURES.length)}
                  aria-hidden={i >= INVENTORY_FEATURES.length}
                >
                  <span className="landing-feature-icon">{f.icon}</span>
                  <div className="landing-feature-title">{f.title}</div>
                  <div className="landing-feature-desc">{f.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </section>

      <section className="landing-section landing-section-alt">
        <div className="landing-texture landing-texture-lattice landing-texture-left" aria-hidden="true" />
        <div className="landing-section-inner">
          <Reveal>
            <span className="landing-section-eyebrow">Purchase &amp; Sales</span>
            <h2>Buy and sell without the paperwork chase.</h2>
            <p className="landing-section-sub">Every invoice and return, tracked and numbered for you.</p>
          </Reveal>
          {/* Two lanes, each a short numbered timeline. No card grid. */}
          <div className="landing-lanes">
            {([
              { label: 'When you buy',  steps: BUY_STEPS,  color: 0 },
              { label: 'When you sell', steps: SELL_STEPS, color: 1 },
            ]).map(lane => (
              <Reveal key={lane.label}>
                <div className="landing-lane" style={iconColor(lane.color)}>
                  <div className="landing-lane-label">{lane.label}</div>
                  <ol className="landing-lane-steps">
                    {lane.steps.map((st, i) => (
                      <li key={st.title} className="landing-lane-step">
                        <span className="landing-lane-dot">{st.icon}<span className="landing-lane-num">{i + 1}</span></span>
                        <div>
                          <div className="landing-lane-title">{st.title}</div>
                          <p className="landing-lane-desc">{st.desc}</p>
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <div className="landing-both">
              <span className="landing-both-label">Works for both</span>
              {BOTH_FEATURES.map((f, i) => (
                <span key={f.title} className="landing-both-item" style={iconColor(i + 3)}>
                  <span className="landing-both-icon">{f.icon}</span>
                  <strong>{f.title}</strong>
                  <span className="landing-both-desc">{f.desc}</span>
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Your data is safe: a short promise on the left, and the layers of
          protection on the right, stacked and narrowing in towards "Your
          data". No other section uses a stack. */}
      <section id="security" className="landing-section">
        <div className="landing-texture landing-texture-dots" aria-hidden="true" />
        <div className="landing-section-inner">
          <div className="landing-safe">
            <Reveal>
              <div className="landing-safe-intro">
                {/* Label, then a row with the big padlock beside the heading.
                    It keeps locking and relocking while on screen. */}
                <span className="landing-section-eyebrow">Security</span>
                <div className="landing-safe-head">
                  <span className="landing-safe-lock">
                    <LockMark size={48} />
                  </span>
                  <h2>Your data stays yours.</h2>
                </div>
                <p>Everything you record, from stones and prices to customers, belongs to you. It stays in your own secure database, guarded at every step.</p>
                {/* The promise as a deep blue seal card: the one dark element
                    in the section, so it reads as the headline commitment. */}
                <div className="landing-safe-seal">
                  <div className="landing-demo-gem landing-safe-seal-gem" aria-hidden="true" />
                  <span className="landing-safe-seal-icon"><IconShieldL /></span>
                  <span className="landing-safe-seal-label">Our promise</span>
                  <p className="landing-safe-seal-text">
                    Your business data belongs to you. We never sell it, share it or use it for anything else.
                  </p>
                </div>
              </div>
            </Reveal>
            <ol className="landing-safe-stack">
              {SAFETY_LAYERS.map((l, i) => (
                <li key={l.title} style={{ '--layer': i } as React.CSSProperties}>
                  <Reveal delay={i * 70}>
                    <div className="landing-safe-layer">
                      <span className="landing-acct-icon" style={iconColor(i + 1)}>{l.icon}</span>
                      <div>
                        <div className="landing-acct-title">{l.title}</div>
                        <p className="landing-acct-desc">{l.desc}</p>
                      </div>
                    </div>
                  </Reveal>
                </li>
              ))}
              <li className="landing-safe-core" style={{ '--layer': SAFETY_LAYERS.length } as React.CSSProperties}>
                <Reveal delay={SAFETY_LAYERS.length * 70}>
                  <div className="landing-safe-core-pill"><IconGemSmall /> Your data</div>
                </Reveal>
              </li>
            </ol>
          </div>
        </div>
      </section>

      <section id="accounting" className="landing-section landing-section-alt">
        <div className="landing-texture landing-texture-lattice" aria-hidden="true" />
        <div className="landing-section-inner">
          <Reveal>
            <span className="landing-section-eyebrow">Accounting</span>
            <h2>Real accounting. Zero accounting headaches.</h2>
            <p className="landing-section-sub">
              Your team works in plain business language. Your accountant gets the real books,
              whenever they need them.
            </p>
          </Reveal>
          {/* Not another grid of boxes: a small Profit & Loss preview on the
              left (what you get), and the four points as a plain list on the
              right. Sample figures only. */}
          <div className="landing-acct">
            <Reveal>
              <div className="landing-acct-visual" aria-hidden="true">
                <div className="landing-acct-glow" />
                <div className="landing-acct-panel">
                  <div className="landing-acct-panel-head">
                    <span>Profit &amp; Loss</span>
                    <span className="landing-acct-chip">This year</span>
                  </div>
                  <div className="landing-acct-figures">
                    <div><span>Money in</span><strong className="in">$248,500</strong></div>
                    <div><span>Money out</span><strong className="out">$193,200</strong></div>
                  </div>
                  <div className="landing-acct-bars">
                    {[42, 58, 50, 71, 64, 86].map((h, i) => (
                      <span key={i} style={{ height: `${h}%` }} className={i === 5 ? 'best' : undefined} />
                    ))}
                  </div>
                  <div className="landing-acct-bars-axis">
                    {['May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct'].map(m => <span key={m}>{m}</span>)}
                  </div>
                  <div className="landing-acct-profit">
                    <span>Profit</span>
                    <strong>$55,300</strong>
                  </div>
                </div>
              </div>
            </Reveal>

            <ul className="landing-acct-list">
              {ACCOUNTING_POINTS.map((pt, i) => (
                <li key={pt.title}>
                  <Reveal delay={i * 80}>
                    <div className="landing-acct-row">
                      <span className="landing-acct-icon" style={iconColor(pt.color)}>{pt.icon}</span>
                      <div>
                        <div className="landing-acct-title">
                          {pt.title}
                          {pt.tag && <span className="landing-acct-tag">{pt.tag}</span>}
                        </div>
                        <p className="landing-acct-desc">{pt.desc}</p>
                      </div>
                    </div>
                  </Reveal>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="landing-section">
        <div className="landing-texture landing-texture-dots" aria-hidden="true" />
        <div className="landing-section-inner">
          <Reveal>
            <span className="landing-section-eyebrow">Platform</span>
            <h2>Built for your whole team.</h2>
            <p className="landing-section-sub">Multiple businesses, staff roles, and a workspace that looks the way you want it to.</p>
          </Reveal>
          {/* A hub diagram: the business in the middle, the three groups
              around it on fine dashed lines. Different from every other
              section (no boxes, no preview panel). */}
          <div className="landing-hub">
            {TEAM_CLUSTERS.map((c, i) => (
              <Reveal key={c.title} delay={i * 90} className={`landing-hub-node landing-hub-node-${i}`}>
                <span className="landing-acct-icon" style={iconColor(i + 3)}>{c.icon}</span>
                <div className="landing-acct-title">{c.title}</div>
                <ul className="landing-team-checks">
                  {c.items.map(item => <li key={item}>{item}</li>)}
                </ul>
              </Reveal>
            ))}
            <div className="landing-hub-core" aria-hidden="true">
              <span className="landing-hub-ring r1" />
              <span className="landing-hub-ring r2" />
              <span className="landing-hub-ring r3" />
              <img src={appLogo} alt="" className="landing-hub-logo" />
            </div>
          </div>
        </div>
      </section>

      <section id="faq" className="landing-section landing-section-alt">
        <div className="landing-texture landing-texture-lattice landing-texture-left" aria-hidden="true" />
        <div className="landing-section-inner">
          {/* Two columns: a short intro on the left, a clean question list on the right. */}
          <div className="landing-faq-layout">
            <Reveal>
              <div className="landing-faq-intro">
                <span className="landing-section-eyebrow">FAQ</span>
                <h2>Frequently asked questions.</h2>
                <p>Everything people usually ask before getting started.</p>
                <div className="landing-faq-help">
                  <div className="landing-faq-help-title">Still have questions?</div>
                  <a href={`https://wa.me/${WHATSAPP_NUMBERS[0].wa}`} target="_blank" rel="noopener noreferrer" className="landing-faq-help-link whatsapp">
                    <IconWhatsApp /> Chat on WhatsApp
                  </a>
                  <a href={`mailto:${CONTACT_EMAIL}`} className="landing-faq-help-link">
                    <IconMail /> {CONTACT_EMAIL}
                  </a>
                </div>
              </div>
            </Reveal>
            <Reveal>
              <div className="landing-faq-list">
                {FAQS.map(f => <FaqItem key={f.q} q={f.q} a={f.a} />)}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Request a demo: one deep blue band, the turning diamond on the side,
          and one clear action. Replaces the old Get in touch cards; the FAQ
          already carries the WhatsApp and email links. */}
      <section id="contact" className="landing-demo">
        <div className="landing-demo-gem" aria-hidden="true" />
        <div className="landing-demo-inner">
          {/* Left: the ask and the buttons. Right: how the demo works, in
              three steps, so "free" and "20 minutes" are each said once. */}
          <div className="landing-demo-grid">
            <Reveal>
              <span className="landing-demo-eyebrow">Free demo</span>
              <h2>See DiaWolt with your own stock.</h2>
              <p>No cost and no obligation. We walk you through it, step by step.</p>
              <div className="landing-demo-actions">
                <a href={DEMO_URL} target="_blank" rel="noopener noreferrer" className="landing-demo-btn">
                  <IconWhatsApp /> Request a demo
                </a>
                <span className="landing-demo-or">
                  or email{' '}
                  <a href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent('DiaWolt demo request')}`} className="landing-demo-link">
                    {CONTACT_EMAIL}
                  </a>
                </span>
              </div>
            </Reveal>
            <Reveal delay={120}>
              <div className="landing-demo-steps">
                <div className="landing-demo-steps-title">How it works</div>
                <ol>
                  {DEMO_STEPS.map((st, i) => (
                    <li key={st.title}>
                      <span className="landing-demo-step-no">{i + 1}</span>
                      <div>
                        <div className="landing-demo-step-title">{st.title}</div>
                        <div className="landing-demo-step-desc">{st.desc}</div>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Dark footer, carrying on from the blue demo band: brand on the
          left, then both phone numbers (tap to call) and the email in one row. */}
      <footer className="landing-footer">
        <div className="landing-footer-inner landing-footer-inner-2">
          <div className="landing-footer-brand">
            <div className="landing-brand">
              <img src={appLogo} alt="" className="landing-brand-icon landing-brand-icon-sm" />
              <span className="landing-brand-text">DiaWolt</span>
            </div>
            <p className="landing-footer-tagline">Stock, sales and books in one place, made for diamond traders.</p>
            <Link to="/login" className="landing-footer-login">Log In</Link>
          </div>

          <div className="landing-footer-col">
            <div className="landing-footer-heading">Call us</div>
            <div className="landing-footer-contacts">
            {WHATSAPP_NUMBERS.map(n => (
              <a key={n.tel} href={`tel:${n.tel}`} className="landing-footer-contact">
                <span className="landing-footer-contact-icon"><IconPhone /></span>
                <span>
                  <span className="landing-footer-contact-label">{n.country}</span>
                  {n.display}
                </span>
              </a>
            ))}
            <a href={`mailto:${CONTACT_EMAIL}`} className="landing-footer-contact">
              <span className="landing-footer-contact-icon"><IconMail /></span>
              <span>
                <span className="landing-footer-contact-label">Email</span>
                {CONTACT_EMAIL}
              </span>
            </a>
            </div>
          </div>
        </div>
        <div className="landing-footer-bottom">
          <span>© {new Date().getFullYear()} DiaWolt. All rights reserved.</span>
          <span className="legal-footer-links">
            {/* Open in a new tab so the visitor keeps their place here. */}
            <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
            <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
          </span>
        </div>
      </footer>
    </div>
  );
}
